import { Recapt } from '../engine';
import { RequestManager } from '../managers/requestManager';
import { WebWorker } from '../managers/webWorker';
import { StringObfuscationRegexes } from '../obfuscateRules';
import { BaseEvent, BaseModule } from './base';
import { ConsoleType, Namespace } from './types';

/**
 * Represents a console event in the system.
 * Extends BaseEvent with console-specific properties.
 */
interface LoggerEvent extends BaseEvent {
  /** Type of console operation */
  kind: string;
  /** Array of message strings from the console operation */
  messages: string[];
}

const ONE_MB = 1000000;

/** List of JavaScript primitive types for value conversion */
const PRIMITIVE_TYPES = [
  'string',
  'number',
  'boolean',
  'undefined',
  'symbol',
  'bigint',
  'null',
];

const PotentialConsoleTypePainpoints = [ConsoleType.ERROR, ConsoleType.WARN];

const skipLogsWithPrefix = [
  '[recapt]',
  '[recapt:rrweb]',
  '[Fast Refresh]',
  '[HMR]',
  '[vite]',
  '[webpack',
];

const CAPTURED_CONSOLE_TYPES = new Set([
  ConsoleType.ERROR,
  ConsoleType.WARN,
  ConsoleType.LOG,
  ConsoleType.INFO,
  ConsoleType.DEBUG,
  ConsoleType.ASSERT,
]);

/**
 * Duck-type check for Error-like objects. Avoids `instanceof` which
 * breaks across iframes / different execution contexts and is no
 * faster than two `typeof` property checks for this use case.
 */
const isErrorLike = (v: unknown): v is Error =>
  v !== null &&
  typeof v === 'object' &&
  typeof (v as Error).message === 'string' &&
  typeof (v as Error).stack === 'string';

/**
 * Converts an Error-like object to a plain object with its key properties.
 * Error properties (message, stack, name) are non-enumerable,
 * so JSON.stringify(new Error("...")) returns "{}".
 */
const serializeError = (err: Error): Record<string, unknown> => ({
  name: err.name,
  message: err.message,
  stack: err.stack,
});

/**
 * Module for intercepting and recording console operations.
 * Proxies console methods to capture their output.
 */
export class LoggerModule extends BaseModule<LoggerEvent> {
  public namespace: Namespace = 'logger';

  /** Indicates if the module is currently recording console events */
  private running = false;
  /** Indicates if console methods have been proxied */
  private hasBound = false;

  private worker?: WebWorker;

  /**
   * Handles console events by creating and storing console event data.
   * Converts complex objects to strings for storage.
   * @param kind - The type of console operation
   * @param rest - Arguments passed to the console method
   */
  onLog(kind: ConsoleType, ...rest: any[]) {
    if (
      typeof rest[0] === 'string' &&
      skipLogsWithPrefix.some((prefix) => rest[0].includes(prefix))
    )
      return;

    const timestamp = Recapt.getCurrentSessionTimestamp();

    if (kind === ConsoleType.CLEAR) {
      this.state.push({
        timestamp,
        kind,
        messages: ['Console was cleared.'],
        where: Recapt.where(),
      });
      return;
    }

    const values = rest.map((v) => {
      if (v === null) return 'null';
      if (v === undefined) return 'undefined';
      if (PRIMITIVE_TYPES.includes(typeof v)) {
        try {
          return v.toString();
        } catch (err) {}

        return v;
      }

      // Error objects have non-enumerable properties (message, stack, name),
      // so JSON.stringify(new Error("...")) returns "{}". Handle them explicitly.
      // Uses duck-type check so errors from iframes are also caught.
      if (isErrorLike(v)) {
        try {
          return JSON.stringify(serializeError(v));
        } catch (err) {
          return v.message || String(v);
        }
      }

      try {
        // Use a replacer to handle nested Error objects inside plain objects
        return JSON.stringify(v, (_key, value) => {
          if (isErrorLike(value)) return serializeError(value);
          return value;
        });
      } catch (err) {
        return '';
      }
    });

    const totalSize = values.reduce(
      (acc, val) => acc + new Blob([val]).size,
      0,
    );

    if (totalSize > ONE_MB) return;

    this.worker?.emit('log', {
      kind,
      messages: values,
      timestamp,
      where: Recapt.where(),
    });
  }

  onRejection(reason: any) {
    try {
      if ('error' in reason) {
        Recapt.debug('onRejection error', reason.error);
        this.onLog(ConsoleType.ERROR, reason.error);
      } else if ('reason' in reason) {
        Recapt.debug('onRejection reason', reason.reason);
        this.onLog(ConsoleType.ERROR, reason.reason);
      } else {
        Recapt.debug('onRejection else', reason);
        this.onLog(ConsoleType.ERROR, reason);
      }
    } catch (err) {}
  }

  /**
   * Initializes the module when started.
   * Loads stored console events and sets up console method proxies.
   */
  onStart() {
    if (this.hasBound) return;
    this.hasBound = true;
    this.running = true;

    const stored = this.loadStore<{
      state: LoggerEvent[];
    }>();

    if (stored) {
      this.state = stored.state;
    }

    this.store(() => ({
      state: this.state,
    }));

    this.worker = new WebWorker('logWorker');

    this.worker.start();

    this.worker.emit('regexes:load', StringObfuscationRegexes);

    const that = this;

    this.worker.on<LoggerEvent>('log:parsed', (data) => {
      if (PotentialConsoleTypePainpoints.includes(data.kind as ConsoleType)) {
        RequestManager.potentialPainpoint();
      }

      that.state.push(data);
    });

    function proxy(context: Console, method: Function, kind: ConsoleType) {
      if (!method) return;

      return function (...rest: any[]) {
        method.apply(context, Array.prototype.slice.apply(rest));
        if (that.running) that.onLog(kind, ...rest);
      };
    }

    Recapt.debug('setting up console proxies');

    const applicableMethods = Object.values(ConsoleType).filter((t) =>
      CAPTURED_CONSOLE_TYPES.has(t),
    );

    for (const key in console) {
      if (typeof console[key as keyof typeof console] !== 'function') continue;
      if (!applicableMethods.includes(key as ConsoleType)) continue;

      //@ts-expect-error typescript doesn't like to overwrite console methods
      console[key] = proxy(console, console[key], key);
    }

    window.addEventListener('rejectionhandled', onRejectionHandled);
    window.addEventListener('unhandledrejection', onRejectionUnHandled);
    window.addEventListener('error', onGenericError);
  }

  /**
   * Stops recording console events.
   */
  onStop() {
    this.running = false;
    window.removeEventListener('rejectionhandled', onRejectionHandled);
    window.removeEventListener('unhandledrejection', onRejectionUnHandled);
    window.removeEventListener('error', onGenericError);

    this.worker?.stop();
  }

  /**
   * Resumes recording console events.
   */
  onResume(): void {
    this.running = true;
  }

  /**
   * Pauses recording of console events.
   */
  onPause(): void {
    this.running = false;
  }

  /**
   * Clears all stored console events.
   */
  purgeState(): void {
    this.state = [];
  }

  /**
   * Builds a request payload from the current console events.
   * @param state - Array of console events to include in the request
   * @returns Object containing the console event state
   */
  buildRequest(
    state: LoggerEvent[],
  ): Record<string, unknown> & { state: LoggerEvent[] } {
    return {
      state,
    };
  }
}

const onGenericError = (evt: ErrorEvent) => {
  if (!Recapt.running) return;
  Recapt.getModule<LoggerModule>('logger')?.onRejection(evt);
};

const onRejectionHandled = (evt: Event) => {
  if (!Recapt.running) return;
  Recapt.getModule<LoggerModule>('logger')?.onRejection(evt);
};

const onRejectionUnHandled = (evt: PromiseRejectionEvent) => {
  if (!Recapt.running) return;
  Recapt.getModule<LoggerModule>('logger')?.onRejection(evt);
};
